import { assert } from '@ember/debug';
import { fn } from '@ember/helper';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isNone, isBlank } from '@ember/utils';
import type Model from '@ember-data/model';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { join } from '@nullvoxpopuli/ember-composable-helpers';
import truncate from 'ember-cli-string-helpers/helpers/truncate';
import { task, dropTask, restartableTask, timeout } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { and, eq, or } from 'ember-truth-helpers';
import BlocklistFilterModal from 'garaje/components/blocklist-filter-modal';
import BlocklistFiltersSearchInput from 'garaje/components/security/configure-blocklist/search-input';
import SlideToggle from 'garaje/components/slide-toggle';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import ButtonGroup from 'garaje/components/ui/button-group';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import VirtualEach from 'garaje/components/virtual-each';
import dateFormat from 'garaje/helpers/date-format';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type User from 'garaje/models/user';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type ExporterService from 'garaje/services/exporter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type { RecordArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import LiquidUnless from 'liquid-fire/components/liquid-unless';

const MINIMUM_BLOCKLIST_RECORD_THRESHOLD = 2;

interface SecurityConfigureBlocklistSignature {
  Args: {
    /**
     * array (or array-like Ember Data result) of all blocklist filters to display
     */
    blocklistFilters: BlacklistFilter[];
    /**
     * string representing the route to the blocklist CSV upload page
     */
    csvImportRoute: string;
    /**
     * string with the name of the relationship to which a new filter entry should be saved (e.g., "location" or "property")
     */
    filterParentRelationshipName: string;
    /**
     * Ember Data model of the parent to which a new filter entry should be attached
     */
    filterParent: Model;
    /**
     * function triggered when list of contacts is changed or saved. Received a single Boolean argument, indicating whether the list is changed & unsaved (true) or not (false).
     */
    onContactsChange?: (hasChanges: boolean) => void;
    /**
     * function to handle saving new list of blocklist notification contacts. Receives one argument (an array of all currently-selected contacts). If this function returns a promise it is awaited.
     */
    saveBlocklistContacts: (users: User[]) => Promise<Model>;
    /**
     * function to load blocklist filters. should return an array or array-like Ember Data result (or a Promise that resolves to that)
     */
    loadBlocklistFilters: () => Promise<BlacklistFilter[]>;
    /**
     * function to load blocklist contacts. should return an array or array-like Ember Data result (or a Promise that resolves to that)
     */
    loadBlocklistContacts: () => Promise<User[]>;
    /**
     * function to return a filtered list of admins that can be selected as notification contacts. Receives one argument (a string representing the current search query) and should return an array of User models, an array-like Ember Data result, or a Promise that resolves to either of those
     */
    searchUsers: (term: string) => Promise<RecordArray<User>>;
    /**
     * boolean indicating whether the blocklist should be read-only or not (blocklist contacts and filter records)
     */
    readOnly?: boolean;
    /**
     * boolean indicating whether the user has the ability to upload a blocklist CSV
     */
    hasUploadPermission?: boolean;
    /**
     * boolean indicating whether the user has the ability to create blocklist filter records
     */
    hasCreatePermission?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [];
  };
}

/**
 * Base component for common blocklist configuration features. You almost certainly do not want to use
 * this component directly; instead, use one of the more specific components (or create your own as needed):
 * - <Security::ConfigureBlocklist::Location> for managing location/company blocklist
 * - <Security::ConfigureBlocklist::Property> for managing a multi-tenant property blocklist
 */
export default class ConfigureBlocklist extends Component<SecurityConfigureBlocklistSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare uploaderFactory: UploaderFactoryService;
  @service declare exporter: ExporterService;

  @tracked searchText = '';
  @tracked blocklistFilterInModal: BlacklistFilter | null = null;
  @tracked blocklistFilterToDelete: BlacklistFilter | null = null;
  @tracked blocklistFilterToToggle: BlacklistFilter | null = null;
  @tracked blocklistContacts: User[] = [];
  @tracked blocklistFilters: BlacklistFilter[] = [];
  @tracked hasChangedBlocklistContacts = false;
  @tracked tempPhoto: string | ArrayBuffer | null = null;
  @tracked tempId: string | null = null;

  /**
   * Feature flag for blocklist enhancements: expiration dates, toggle, delete, new table layout
   */
  get hasBlocklistEnhancements(): boolean {
    return this.featureFlags.isEnabled('feat-blocklist-expiration-and-pause');
  }

  /**
   * When toggling, if disabled=true (currently unblocked), we're about to BLOCK them.
   * If disabled=false (currently blocked), we're about to UNBLOCK them.
   */
  get isBlockingAction(): boolean {
    return this.blocklistFilterToToggle?.disabled === true;
  }

  get timezone(): string {
    return this.state.currentLocation?.timezone ?? 'America/Los_Angeles';
  }

  /**
   * Tailwind class helpers for new table layout (feature flag: feat-blocklist-expiration-and-pause)
   * Uses Tailwind classes with arbitrary values for dynamic widths
   */
  getRowClass(disabled: boolean): string {
    const base = 'flex items-center gap-5 px-6 min-h-[58px] w-full cursor-pointer border-b border-[#e9e9ea]';
    // bg-[#fcfcfc] is a subtle gray for disabled rows (between white and carbon-5)
    return disabled ? `${base} bg-[#fcfcfc]` : base;
  }

  getAddedOnColumnClass(disabled: boolean): string {
    const base = 'w-[140px] shrink-0';
    return disabled ? `${base} opacity-40` : base;
  }

  getAddedByColumnClass(disabled: boolean): string {
    const base = 'w-[160px] shrink-0';
    return disabled ? `${base} opacity-40` : base;
  }

  getVisitorColumnClass(disabled: boolean): string {
    const base = 'w-[200px] shrink-0 flex items-center gap-2';
    return disabled ? `${base} opacity-40` : base;
  }

  getReasonColumnClass(disabled: boolean): string {
    const base = 'flex-1 min-w-[120px]';
    return disabled ? `${base} opacity-40` : base;
  }

  getExpiresColumnClass(disabled: boolean): string {
    const base = 'w-[120px] shrink-0 whitespace-nowrap flex items-center';
    return disabled ? `${base} opacity-40` : base;
  }

  get aboveMinimumBlocklistRecordThreshold(): boolean {
    return this.blocklistFilters.length > MINIMUM_BLOCKLIST_RECORD_THRESHOLD;
  }

  get sortedBlocklistFilters(): BlacklistFilter[] {
    assert('You must pass @blocklistFilters to <Security::ConfigureBlocklist>', !isNone(this.args.blocklistFilters));

    if (this.hasBlocklistEnhancements) {
      // New sorting: 1) enabled items first (disabled=false), 2) then alphabetically by fullName
      return this.args.blocklistFilters.toSorted((a, b) => {
        // Disabled items go to the bottom
        if (a.disabled !== b.disabled) {
          return a.disabled ? 1 : -1;
        }
        // Within same disabled status, sort alphabetically
        return (a.fullName || '').localeCompare(b.fullName || '');
      });
    }

    // Original sorting: just alphabetically by fullName
    return this.args.blocklistFilters.toSorted((a, b) => {
      return (a.fullName || '').localeCompare(b.fullName || '');
    });
  }

  /*
   * Filters blocklistFilters based on the current value of 'searchText'
   */
  get filteredBlocklistFilters(): BlacklistFilter[] {
    const { searchText, sortedBlocklistFilters } = this;

    if (isBlank(searchText)) {
      return sortedBlocklistFilters;
    }

    const lowerSearchText = searchText.toLowerCase();
    return sortedBlocklistFilters.filter((blocklistFilter) => {
      return blocklistFilter.searchable.toLowerCase().indexOf(lowerSearchText) !== -1;
    });
  }

  async loadBlocklistFilters(): Promise<void> {
    const blocklistFilters = await this.args.loadBlocklistFilters();
    this.blocklistFilters = blocklistFilters.slice();
  }

  @action
  onInsert(): void {
    void this.initBlocklistFiltersTask.perform();
    void this.initBlocklistContactsTask.perform();
  }

  @action
  onDestroy(): void {
    if (
      this.blocklistFilterInModal &&
      Boolean(this.blocklistFilterInModal.isNew) &&
      !this.blocklistFilterInModal.isDestroying
    ) {
      this.blocklistFilterInModal.unloadRecord();
    }
  }

  mutateSearchText = restartableTask(async (searchText: string) => {
    await timeout(zft(200));

    this.searchText = searchText;
  });

  exportBlocklistFiltersAsCsv = dropTask(async () => {
    assert('You must pass @blocklistFilters to <Security::ConfigureBlocklist>', !isNone(this.args.blocklistFilters));
    await timeout(zft(200));
    try {
      await this.exporter.exportCsv.perform('blocklist-export.csv', this.args.blocklistFilters);
      this.flashMessages.showAndHideFlash('success', 'Block list downloaded!');
    } catch {
      this.flashMessages.showFlash('error', 'Block list download failed');
    }
  });

  initBlocklistFiltersTask = task(async () => {
    assert(
      'You must pass a @loadBlocklistFilters function as an argument to <Security::ConfigureBlocklist>',
      typeof this.args.loadBlocklistFilters === 'function',
    );
    try {
      await this.loadBlocklistFilters();
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error', parseErrorForDisplay(e));
    }
  });

  initBlocklistContactsTask = task(async () => {
    assert(
      'You must pass @loadBlocklistContacts to <Security::ConfigureBlocklist>',
      typeof this.args.loadBlocklistContacts === 'function',
    );
    try {
      this.blocklistContacts = await this.args.loadBlocklistContacts();
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error', parseErrorForDisplay(e));
    }
  });

  searchUsersTask = task(async (term: string) => {
    assert('You must pass @searchUsers to <Security::ConfigureBlocklist>', typeof this.args.searchUsers === 'function');
    const currentContactsIds = this.blocklistContacts.map((user) => user.id);
    const users = await this.args.searchUsers(term);

    const lowerSearchText = term.toLowerCase();

    return users
      .uniqBy('id') // dedup since roles could point to same user
      .reject(({ id }) => currentContactsIds.includes(id)) // remove existing contacts
      .filter(({ fullName }) => fullName.toLowerCase().indexOf(lowerSearchText) !== -1);
  });

  saveBlocklistContactsTask = task(async () => {
    assert(
      'You must pass @saveBlocklistContacts to <Security::ConfigureBlocklist>',
      typeof this.args.saveBlocklistContacts === 'function',
    );

    try {
      await this.args.saveBlocklistContacts(this.blocklistContacts);
      this.hasChangedBlocklistContacts = false;
      this.args.onContactsChange?.(false);
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error adding administrator', 'Please try again.');
    }
  });

  uploadPhotoTask = task(async (photo: File, id: string) => {
    try {
      const uploader = this.uploaderFactory.createUploader({
        url: urlBuilder.v3.blocklistFilter.photoUpload(id),
        method: 'POST',
        headers: { Accept: 'application/vnd.api+json' },
      });
      await uploader.upload(photo);
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error uploading photo', 'Please try again.');
    }
  });

  deletePhotoTask = task(async (id: string) => {
    try {
      await this.ajaxFetch.del(urlBuilder.v3.blocklistFilter.photoUpload(id));
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error removing photo', 'Please try again.');
    }
  });

  saveBlocklistFilterTask = task(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
    const isNew = Boolean(blocklistFilter.isNew);
    const editedFields = Object.keys(blocklistFilter.changedAttributes());
    const shouldDeletePhoto =
      !isNew &&
      !photo &&
      !blocklistFilter.photo.dataUrl &&
      !blocklistFilter.photo.original &&
      editedFields.includes('photo');

    try {
      if (photo) this.tempPhoto = blocklistFilter.photo.dataUrl;

      this.metrics.trackJobEvent(`Security - Blocked Person ${isNew ? 'Creation' : 'Update'} Requested`, {
        block_list_filter_id: blocklistFilter.id,
        edited_fields: editedFields,
      });

      if (shouldDeletePhoto) {
        // delete the photo before we save the record so that the photo is gone by the time the save updates the record
        await this.deletePhotoTask.perform(blocklistFilter.id);
      }

      await blocklistFilter.save();
      await this.loadBlocklistFilters();

      this.metrics.trackJobEvent(`Security - Blocked Person Record ${isNew ? 'Created' : 'Updated'}`, {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'success',
      });

      this.tempId = blocklistFilter.id;
      this.blocklistFilterInModal = null;

      const message_title = isNew ? 'Saved!' : 'Updated!';

      this.flashMessages.showAndHideFlash('success', message_title);
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'success',
        message_title,
        message_code: isNew ? 'blocked_person_create_success' : 'blocked_person_update_success',
        message_code_type: 'block_list',
      });

      if (photo) {
        await this.uploadPhotoTask.perform(photo, blocklistFilter.id);
        await blocklistFilter.reload(); // reload the filter to get the uploaded photo
      }

      this.tempPhoto = null;
      this.tempId = null;
    } catch (e) {
      const error_message = parseErrorForDisplay(e);
      this.metrics.trackJobEvent(`Security - Blocked Person Record ${isNew ? 'Created' : 'Updated'}`, {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'failure',
        error_message,
      });

      const message_title = isNew ? 'Save failed' : 'Update failed';
      this.flashMessages.showAndHideFlash('error', message_title, 'Please try again.');

      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'error',
        message_title,
        message_code: error_message,
        message_code_type: 'block_list',
      });
    }
  });

  deleteBlocklistFilterTask = task(async (blocklistFilter: BlacklistFilter) => {
    this.metrics.startJob();
    try {
      this.metrics.trackJobEvent(`Security - Blocked Person Deletion Requested`, {
        block_list_filter_id: blocklistFilter.id,
      });

      const updatedBlocklistFilters = this.blocklistFilters.filter((item) => item.id !== blocklistFilter.id);
      await blocklistFilter.destroyRecord();
      this.blocklistFilters = updatedBlocklistFilters;
      this.blocklistFilterInModal = null;

      this.metrics.trackJobEvent('Security - Blocked Person Record Deleted', {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'success',
      });

      const message_title = 'Deleted record';
      this.flashMessages.showAndHideFlash('success', message_title);
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'success',
        message_title,
        message_code: 'blocked_person_delete_success',
        message_code_type: 'block_list',
      });
    } catch (e) {
      const error_message = parseErrorForDisplay(e);
      this.metrics.trackJobEvent('Security - Blocked Person Record Deleted', {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'failure',
        error_message,
      });

      const message_title = 'Delete failed';
      this.flashMessages.showAndHideFlash('error', message_title, 'Please try again.');
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'error',
        message_title,
        message_code: error_message,
        message_code_type: 'block_list',
      });
    }
  });

  @action
  createBlocklistFilter(): void {
    assert(
      'You must pass @filterParentRelationshipName to <Security::ConfigureBlocklist>',
      !isNone(this.args.filterParentRelationshipName),
    );
    assert('You must pass @filterParent function to <Security::ConfigureBlocklist>', !isNone(this.args.filterParent));
    const newBlocklistFilter = this.store.createRecord('blacklist-filter', {
      [this.args.filterParentRelationshipName]: this.args.filterParent,
    });
    this.blocklistFilterInModal = newBlocklistFilter;
    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Nav to Add New Blocked Person Button Clicked', { button_text: 'Add new' });
  }

  @action
  editBlocklistFilter(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterInModal = blocklistFilter;
    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Nav to Edit Blocked Person Button Clicked', { button_text: 'Edit' });
  }

  @action
  closeBlocklistFilterModal(blocklistFilter: BlacklistFilter): void {
    blocklistFilter?.rollbackAttributes();
    this.blocklistFilterInModal = null;
  }

  @action
  showToggleConfirmation(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterToToggle = blocklistFilter;
  }

  @action
  closeToggleConfirmation(): void {
    this.blocklistFilterToToggle = null;
  }

  confirmToggleTask = task(async () => {
    const blocklistFilter = this.blocklistFilterToToggle;
    if (!blocklistFilter) return;

    const isBlocking = blocklistFilter.disabled; // disabled=true means currently unblocked, so we're blocking
    const personName = blocklistFilter.fullName || '';
    const truncatedName = personName.length > 40 ? `${personName.slice(0, 40)}...` : personName;

    // Close modal immediately to prevent flash when state changes
    this.blocklistFilterToToggle = null;

    try {
      blocklistFilter.disabled = !blocklistFilter.disabled;
      await blocklistFilter.save();

      if (isBlocking) {
        this.flashMessages.showAndHideFlash('success', 'Success', `${truncatedName} has been blocked.`);
      } else {
        this.flashMessages.showAndHideFlash('success', 'Success', `${truncatedName} has been unblocked.`);
      }
    } catch (_e) {
      blocklistFilter.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', 'Update failed', 'Please try again.');
    }
  });

  @action
  updateBlocklistContacts(users: User[]): void {
    this.hasChangedBlocklistContacts = true;
    this.args.onContactsChange?.(true);
    this.blocklistContacts = users;
  }

  @action
  showDeleteConfirmation(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterToDelete = blocklistFilter;
  }

  @action
  closeDeleteConfirmation(): void {
    this.blocklistFilterToDelete = null;
  }

  confirmDeleteTask = task(async () => {
    const filter = this.blocklistFilterToDelete;
    if (filter) {
      try {
        await this.deleteBlocklistFilterTask.perform(filter);
      } finally {
        this.blocklistFilterToDelete = null;
      }
    }
  });

  <template>
    <fieldset
      class="pb-8 min-w-0 space-y-8"
      {{didInsert this.onInsert}}
      {{willDestroy this.onDestroy}}
      data-test-blocklist-settings
    >
      <div class="mr-16">
        <h4>Alerts</h4>
        <div class="flow-root row">
          <div class="float-left box-border w-10/12">
            <p class="mb-8">
              {{if
                @readOnly
                "These administrators are alerted when a visitor matches the block list."
                "Choose which administrators to alert when a visitor is a blocklist match."
              }}
            </p>
            <div class="flex">
              <div class="grow max-w-full" data-test-blocklist-contact-input>
                <PowerSelectMultiple
                  @ariaLabel="Choose admins to receive block list alerts"
                  @search={{this.searchUsersTask.perform}}
                  @searchEnabled={{true}}
                  @searchField="fullName"
                  @placeholder="Search administrators"
                  @allowClear={{true}}
                  @selected={{this.blocklistContacts}}
                  @onChange={{this.updateBlocklistContacts}}
                  @disabled={{@readOnly}}
                  as |blocklistContact|
                >
                  {{blocklistContact.fullName}}
                </PowerSelectMultiple>
              </div>

              <button
                data-test-single-save
                disabled={{this.saveBlocklistContactsTask.isRunning}}
                class="btn btn--small btn--brand ml-4 self-start
                  {{unless this.hasChangedBlocklistContacts 'invisible'}}"
                {{on "click" this.saveBlocklistContactsTask.perform}}
              >
                Save contacts
              </button>
            </div>

            <div class="text-sm text-carbon-50 mt-4">
              {{#if (has-block "admin-permission-text")}}
                {{yield to="admin-permission-text"}}
              {{/if}}
              {{#unless @readOnly}}
                Administrators can set notification preferences for block list alerts in their
                <LinkTo @route="profile.notifications">notification settings</LinkTo>.
              {{/unless}}
            </div>
          </div>
        </div>
      </div>

      <div class="mr-8 pt-16 border-t">
        <div class="flex items-center">
          <div class="flex-auto">
            <h4>People you block</h4>
            <p class="mb-0">
              These people will require approval to visit any of your locations where block list is enabled.
            </p>
          </div>

          <ButtonGroup class="shrink-0">
            {{#if this.initBlocklistFiltersTask.isIdle}}
              {{#unless @readOnly}}
                <button
                  data-test-blocklist-filter-export-button
                  type="button"
                  class="btn btn--small btn--outline"
                  disabled={{this.exportBlocklistFiltersAsCsv.isRunning}}
                  {{on "click" this.exportBlocklistFiltersAsCsv.perform}}
                >
                  Export
                </button>
              {{/unless}}
              {{#if (or @hasCreatePermission @hasUploadPermission)}}
                <div>
                  <DropdownMenu @left="-34" @top="10" @width="180">
                    <:trigger>
                      <button
                        data-test-blocklist-filter-add-record-menu-button
                        class="btn btn--small btn--brandOutline split-drop-button"
                        role="button"
                      >
                        Add records
                        <img
                          src="/assets/images/chevron-down.svg"
                          alt="show actions"
                          class="ml-2"
                          style="margin-bottom: 2px;"
                        />
                      </button>
                    </:trigger>
                    <:content as |itemClasses|>
                      {{#if @hasCreatePermission}}
                        <button
                          data-test-blocklist-filter-add-single-record-button
                          class="ui-button {{itemClasses}}"
                          role="button"
                          {{on "click" this.createBlocklistFilter}}
                        >
                          Add a single record
                        </button>
                      {{/if}}
                      {{#if @hasUploadPermission}}
                        <LinkTo
                          data-test-blocklist-filter-import-csv-button
                          class="ui-button {{itemClasses}}"
                          role="button"
                          @route={{@csvImportRoute}}
                        >
                          Import from CSV
                        </LinkTo>
                      {{/if}}
                    </:content>
                  </DropdownMenu>
                </div>
              {{/if}}
            {{/if}}
          </ButtonGroup>
        </div>

        {{#if (has-block "message")}}
          {{yield to="message"}}
        {{/if}}

        <LiquidUnless @predicate={{this.initBlocklistFiltersTask.isRunning}} @use="fade">
          <div class="flex items-center block-search-row space-x-4 h-18 w-full">
            <div class="flex-auto">
              {{this.filteredBlocklistFilters.length}}
              blocked records
              {{#if this.searchText}} matching "{{this.searchText}}"{{/if}}
            </div>
            <BlocklistFiltersSearchInput
              @name="search-blocklist-filters"
              @placeholder="Search blocklist records"
              @value={{this.searchText}}
              @onChange={{this.mutateSearchText}}
              @showClear={{if this.searchText true false}}
              @enabled={{this.aboveMinimumBlocklistRecordThreshold}}
              style="width:350px;"
            />
          </div>
        </LiquidUnless>
        {{#if this.hasBlocklistEnhancements}}
          {{! NEW TABLE - Feature: feat-blocklist-expiration-and-pause }}
          <div class="csv-table mt-2">
            {{! Table header - uses inline styles for border color not in design system (#e9e9ea) }}
            <div
              class="flex items-center gap-5 px-6 py-3 border-b w-full font-semibold text-sm leading-snug bg-carbon-5 text-carbon-50"
              style="border-bottom-color: #e9e9ea;"
            >
              <div style="width: 200px; flex-shrink: 0;">Visitor name</div>
              <div style="width: 140px; flex-shrink: 0;">Added on</div>
              <div style="width: 160px; flex-shrink: 0;">Added by</div>
              <div style="flex: 1; min-width: 120px;">Reason</div>
              <div style="width: 120px; flex-shrink: 0; text-align: center;">Expires after</div>
              <div style="width: 70px; flex-shrink: 0; text-align: center;">Blocked</div>
              <div style="width: 72px; flex-shrink: 0;">
                <span class="sr-only">{{if @readOnly "View" "Edit"}}</span>
              </div>
            </div>
            <div class="csv-table-body text-sm">

              <VirtualEach
                @items={{this.filteredBlocklistFilters}}
                @height={{580}}
                @itemHeight={{58}}
                @positionIndex={{0}}
              >
                <:default as |blocklistFilter index|>
                  <div
                    class={{this.getRowClass blocklistFilter.disabled}}
                    data-test-blocklist-filter-id={{blocklistFilter.id}}
                    data-test-blocklist-filter-row={{index}}
                  >
                    {{! Visitor name (photo + name) - NEW column, no CSS conflict }}
                    <div
                      role="button"
                      class={{this.getVisitorColumnClass blocklistFilter.disabled}}
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-fullname={{index}}
                    >
                      {{#if (and (eq blocklistFilter.id this.tempId) this.tempPhoto)}}
                        <div
                          data-test-blocklist-filter-row-photo-temp
                          class="rounded-full border blur-sm bg-cover bg-center flex-shrink-0"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" this.tempPhoto ");")
                          }}
                        >
                          <span class="sr-only">Photo upload in progress</span>
                        </div>
                      {{else if blocklistFilter.photo.thumb}}
                        {{! Photo thumbnail as div (not link) - nested interactive elements are invalid for a11y.
                            Full-size photo is viewable in the edit modal. }}
                        <div
                          data-test-blocklist-filter-row-photo
                          class="rounded-full border bg-cover bg-center flex-shrink-0"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" blocklistFilter.photo.thumb ");")
                          }}
                        >
                          <span class="sr-only">Photo thumbnail</span>
                        </div>
                      {{else}}
                        <div
                          data-test-blocklist-filter-row-photo-default
                          class="rounded-full border bg-carbon-40 bg-no-repeat bg-center flex-shrink-0"
                          style="width:40px; height:40px; background-image:url(/assets/images/profile.svg)"
                        >
                          <span class="sr-only">No photo available</span>
                        </div>
                      {{/if}}
                      <span class="font-medium text-carbon-70">
                        <TruncateWithTooltip @value={{or blocklistFilter.fullName ""}} @length={{30}} @side="bottom" />
                      </span>
                    </div>

                    {{! Added on (date + time) - NEW column, no CSS conflict }}
                    <div
                      role="button"
                      class={{this.getAddedOnColumnClass blocklistFilter.disabled}}
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                    >
                      <div class="flex flex-col">
                        {{! template-lint-disable attribute-indentation }}
                        <span class="text-carbon-70">{{dateFormat
                            blocklistFilter.createdAt
                            "MMM d, yyyy"
                            timezone=this.timezone
                          }}</span>
                        <span class="text-carbon-50">{{dateFormat
                            blocklistFilter.createdAt
                            "h:mm a zzz"
                            timezone=this.timezone
                          }}</span>
                      </div>
                    </div>

                    {{! Added by (name + role) - NEW column, no CSS conflict }}
                    <div
                      role="button"
                      class={{this.getAddedByColumnClass blocklistFilter.disabled}}
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                    >
                      <div class="flex flex-col justify-center">
                        {{#if blocklistFilter.createdByContext}}
                          <span class="text-carbon-70">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdByContext.name ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                          <span class="text-carbon-50">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdByContext.role_name ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                        {{else}}
                          <span class="text-carbon-70">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdBy ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                        {{/if}}
                      </div>
                    </div>

                    {{! Reason - flexible column that grows to fill space }}
                    <div
                      role="button"
                      class={{this.getReasonColumnClass blocklistFilter.disabled}}
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-reason={{index}}
                    >
                      <TruncateWithTooltip
                        @value={{or blocklistFilter.reason ""}}
                        @length={{32}}
                        @side="bottom"
                        class="text-carbon-70"
                      />
                    </div>

                    {{! Expires after - NEW column, no CSS conflict }}
                    <div
                      role="button"
                      class="{{this.getExpiresColumnClass blocklistFilter.disabled}}
                        text-carbon-70 blocklist-expires-at"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                    >
                      {{if
                        blocklistFilter.expiresAt
                        (dateFormat blocklistFilter.expiresAt "MMM d, yyyy" timezone=this.timezone)
                        "Never"
                      }}
                    </div>

                    {{! Blocked toggle - NEW column, no CSS conflict }}
                    <div
                      class="w-[70px] shrink-0 flex items-center justify-center"
                      data-test-blocklist-filter-row-enabled={{index}}
                    >
                      <SlideToggle
                        class="toggle--tiny"
                        @id={{concat "toggle-" blocklistFilter.id}}
                        @isChecked={{blocklistFilter.isBlocked}}
                        @onClick={{fn this.showToggleConfirmation blocklistFilter}}
                        @isDisabled={{@readOnly}}
                      />
                    </div>

                    {{! Edit and Delete actions }}
                    <div
                      style="width: 72px; flex-shrink: 0; display: flex; align-items: center; justify-content: flex-end; gap: 8px;"
                    >
                      {{#if @readOnly}}
                        <button
                          data-test-blocklist-filter-row-edit={{index}}
                          type="button"
                          class="btn btn--link p-1"
                          {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                        >
                          View
                        </button>
                      {{else}}
                        <button
                          data-test-blocklist-filter-row-edit={{index}}
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Edit"
                          {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                        >
                          <img src="/assets/images/edit-16.svg" alt="edit" class="block w-4 h-4" />
                        </button>
                        <button
                          data-test-blocklist-filter-row-delete={{index}}
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Delete"
                          {{on "click" (fn this.showDeleteConfirmation blocklistFilter)}}
                        >
                          <img src="/assets/images/trash-16.svg" alt="delete" class="block w-4 h-4" />
                        </button>
                      {{/if}}
                    </div>
                  </div>
                </:default>
                <:inverse>
                  <div class="csv-table-empty p-4 text-carbon-50" data-test-blocklist-filter-row-empty-list>
                    {{#if this.initBlocklistFiltersTask.isRunning}}
                      <LoadingSpinnerPulse />
                    {{else if this.initBlocklistFiltersTask.last.isError}}
                      There was a problem loading the block list.
                    {{else if this.blocklistFilters.length}}
                      Your search for "{{this.searchText}}" does not match any records.
                    {{else}}
                      No one blocked yet. Blocked people will appear here.
                    {{/if}}
                  </div>
                </:inverse>
              </VirtualEach>
            </div>
          </div>
        {{else}}
          {{! ORIGINAL TABLE - when feature flag is off }}
          <div class="csv-table mt-2">
            <div class="csv-table-header-row flex text-xs text-carbon-50 font-bold">
              <div class="blocklist-photo table-cell py-2 ml-4 mr-3">
                Photo
              </div>
              <div class="blocklist-name table-cell pr-4 pl-3 py-2">
                Full name
              </div>
              <div class="blocklist-aliases table-cell pr-4 pl-3 py-2">
                Aliases
              </div>
              <div class="blocklist-emails table-cell pr-4 pl-3 py-2">
                Email addresses
              </div>
              <div class="blocklist-phones table-cell pr-4 pl-3 py-2">
                Phone Numbers
              </div>
              <div class="blocklist-description table-cell pr-4 pl-3 py-2">
                Description
              </div>
              <div class="blocklist-keywords table-cell pr-4 pl-3 py-2">
                Keywords
              </div>
              <div class="blocklist-reason table-cell pr-4 pl-3 py-2">
                Reason for blocking
              </div>
              <div class="blocklist-action table-cell pr-4 pl-3 py-2">
                <span class="sr-only">{{if @readOnly "View" "Edit"}}</span>
              </div>
            </div>
            <div class="csv-table-body flex text-sm">

              <VirtualEach
                @items={{this.filteredBlocklistFilters}}
                @height={{580}}
                @itemHeight={{58}}
                @positionIndex={{0}}
              >
                <:default as |blocklistFilter index|>
                  <div
                    class="csv-table-row flex border-b"
                    data-test-blocklist-filter-id={{blocklistFilter.id}}
                    data-test-blocklist-filter-row={{index}}
                  >
                    <div class="blocklist-photo table-cell pb-2 pt-1 align-middle ml-4">
                      {{#if (and (eq blocklistFilter.id this.tempId) this.tempPhoto)}}
                        <div
                          data-test-blocklist-filter-row-photo-temp
                          class="block my-1 border blur-2 bg-cover bg-center"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" this.tempPhoto ");")
                          }}
                        >
                          <span class="sr-only">Photo upload in progress </span>
                        </div>
                      {{else if blocklistFilter.photo.thumb}}
                        <a
                          data-test-blocklist-filter-row-photo
                          href={{blocklistFilter.photo.original}}
                          target="_blank"
                          rel="noopener"
                          class="block my-1 border bg-cover bg-center"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" blocklistFilter.photo.thumb ");")
                          }}
                        >
                          <span class="sr-only">View full size photo</span>
                        </a>
                      {{else}}
                        <div
                          data-test-blocklist-filter-row-photo-default
                          class="my-1 border bg-carbon-5 bg-no-repeat bg-center"
                          style="width:40px; height:40px; background-image:url(/assets/images/profile.svg)"
                        >
                          <span class="sr-only">No photo available</span>
                        </div>
                      {{/if}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-name px-4 py-2 align-middle font-normal"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-fullname={{index}}
                    >
                      {{truncate blocklistFilter.fullName 25 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-aliases px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-aliases={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.aliases) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-emails px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-emails={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.emails) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-phones px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-phones={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.phoneNumbers) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-description px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-description={{index}}
                    >
                      {{truncate blocklistFilter.description 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-keywords px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-keywords={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.otherKeywords) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-reason px-4 py-2 align-middle break-words break-all"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-reason={{index}}
                    >
                      <p>{{truncate blocklistFilter.reason 80 true}}</p>
                    </div>
                    <div class="blocklist-action table-cell px-4 py-2 align-middle text-center">
                      <div class="flex items-center h-full">
                        <button
                          data-test-blocklist-filter-row-edit={{index}}
                          type="button"
                          class="btn btn--link"
                          {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                        >
                          {{#if @readOnly}}
                            View
                          {{else}}
                            <img src="/assets/images/pencil.svg" alt="edit" class="p-2 box-content" />
                          {{/if}}
                        </button>
                      </div>
                    </div>
                  </div>
                </:default>
                <:inverse>
                  <div class="csv-table-empty p-4 text-carbon-50" data-test-blocklist-filter-row-empty-list>
                    {{#if this.initBlocklistFiltersTask.isRunning}}
                      <LoadingSpinnerPulse />
                    {{else if this.initBlocklistFiltersTask.last.isError}}
                      There was a problem loading the block list.
                    {{else if this.blocklistFilters.length}}
                      Your search for "{{this.searchText}}" does not match any records.
                    {{else}}
                      No one blocked yet. Blocked people will appear here.
                    {{/if}}
                  </div>
                </:inverse>
              </VirtualEach>
            </div>
          </div>
        {{/if}}
      </div>
    </fieldset>

    {{#if this.blocklistFilterInModal}}
      <BlocklistFilterModal
        @blocklistFilter={{this.blocklistFilterInModal}}
        @close={{fn this.closeBlocklistFilterModal this.blocklistFilterInModal}}
        @delete={{fn this.deleteBlocklistFilterTask.perform this.blocklistFilterInModal}}
        @isSaving={{this.saveBlocklistFilterTask.isRunning}}
        @maxPhotoSizeKb={{10000}}
        @save={{fn this.saveBlocklistFilterTask.perform this.blocklistFilterInModal}}
        @readOnly={{@readOnly}}
      />
    {{/if}}

    {{#if this.hasBlocklistEnhancements}}
      {{! NEW MODALS - Feature: feat-blocklist-expiration-and-pause }}
      {{#if this.blocklistFilterToDelete}}
        <ConfirmationModal
          @title="Delete blocked person"
          @ctaText="Delete"
          @handleTaskAction={{this.confirmDeleteTask}}
          @onClose={{this.closeDeleteConfirmation}}
          @closeButtonClass="btn--outline hover:bg-carbon-5"
          @showTopRightCloseButton={{true}}
          @containerClass="!max-w-[550px]"
          data-test-delete-blocklist-filter-modal
        >
          <p class="mt-4 text-carbon-60">
            Are you sure you want to delete
            <strong class="text-carbon-70">{{this.blocklistFilterToDelete.fullName}}</strong>
            from the block list? This action cannot be undone.
          </p>
        </ConfirmationModal>
      {{/if}}

      {{#if this.blocklistFilterToToggle}}
        <ConfirmationModal
          @title={{if this.isBlockingAction "Block visitor" "Unblock visitor"}}
          @ctaText={{if this.isBlockingAction "Block" "Unblock"}}
          @handleTaskAction={{this.confirmToggleTask}}
          @onClose={{this.closeToggleConfirmation}}
          @closeButtonClass="btn--outline hover:bg-carbon-5"
          @showTopRightCloseButton={{true}}
          @containerClass="!max-w-[550px]"
          data-test-toggle-blocklist-filter-modal
        >
          <p class="mt-4 text-carbon-60">
            {{#if this.isBlockingAction}}
              Are you sure you want to block
              <strong class="text-carbon-70">{{this.blocklistFilterToToggle.fullName}}</strong>? They will be denied
              entry at this location.
            {{else}}
              Are you sure you want to unblock
              <strong class="text-carbon-70">{{this.blocklistFilterToToggle.fullName}}</strong>? They will no longer be
              blocked from entering this location.
            {{/if}}
          </p>
        </ConfirmationModal>
      {{/if}}
    {{/if}}
  </template>
}
